import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/authProvider';
import { useMe, useCustomerIds, useStores, useCartsStatus } from '@/services/queries';
import { initials } from '@/lib/display';
import { getPreviewSession } from '@/lib/preview';
import { Login } from '@/screens/Login';
import { Home } from '@/screens/Home';
import { Switcher } from '@/screens/Switcher';
import { Detail } from '@/screens/Detail';
import { NoAccess } from '@/screens/NoAccess';
import { AccountMenu } from '@/components/AccountMenu';
import { ACTION_NAMES, track } from '@/lib/engagement';
import { useEngagementTracking } from '@/lib/useEngagement';

type Screen = 'switcher' | 'home' | 'detail';

const STORE_KEY = 'zeo-companion-store';
const DARK_KEY = 'zeo-companion-dark';

const TRACK_X: Record<Screen, string> = {
  switcher: 'translateX(0)',
  home: 'translateX(-33.3333%)',
  detail: 'translateX(-66.6667%)',
};

export default function App() {
  const { isAuthenticated } = useAuth();
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === '1');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  const toggleDark = () => {
    setDark((d) => {
      localStorage.setItem(DARK_KEY, d ? '0' : '1');
      return !d;
    });
  };

  return (
    <div className="phone-frame relative mx-auto h-[100dvh] max-w-[430px] overflow-hidden bg-page shadow-phone">
      {isAuthenticated ? <Shell dark={dark} onToggleDark={toggleDark} /> : <Login />}
    </div>
  );
}

function Shell({ dark, onToggleDark }: { dark: boolean; onToggleDark: () => void }) {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const me = useMe();
  const customers = useCustomerIds();
  const { stores, isLoading: storesLoading } = useStores(customers.data ?? []);

  const [screen, setScreen] = useState<Screen>('home');
  const [storeId, setStoreId] = useState<string | null>(() => localStorage.getItem(STORE_KEY));
  const [cartIdx, setCartIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const store = stores.find((s) => s.id === storeId) ?? stores[0] ?? null;
  const cartsQuery = useCartsStatus(store?.id ?? null);
  const carts = cartsQuery.data ?? [];
  const cart = carts[cartIdx] ?? carts[0] ?? null;
  useEngagementTracking(me.data?.email, screen);

  if (me.isLoading || customers.isLoading || storesLoading) return null;

  const avatarInitials = me.data ? initials(me.data.name) : '';
  const accountMenu = menuOpen && me.data && (
    <AccountMenu
      name={me.data.name}
      email={me.data.email}
      dark={dark}
      onToggleDark={onToggleDark}
      onSignOut={logout}
      onClose={() => setMenuOpen(false)}
    />
  );

  if (!store) {
    return (
      <>
        <NoAccess
          email={me.data?.email}
          avatarInitials={avatarInitials}
          onToggleMenu={() => setMenuOpen((o) => !o)}
        />
        {accountMenu}
      </>
    );
  }

  const selectStore = (id: string) => {
    if (id !== store.id) track('card_click', ACTION_NAMES.switchStore);
    // Preview sessions must not clobber the admin's own saved store
    if (!getPreviewSession()) localStorage.setItem(STORE_KEY, id);
    // A store's carts are cached for the session; picking it always refetches
    // so returning to a store never shows data from an earlier visit.
    queryClient.invalidateQueries({ queryKey: ['carts-status', id] });
    setStoreId(id);
    setCartIdx(0);
    setScreen('home');
  };

  return (
    <>
      <div
        className="flex h-full w-[300%]"
        style={{
          transform: TRACK_X[screen],
          transition: 'transform .3s cubic-bezier(.33,0,.15,1)',
        }}
      >
        <Switcher stores={stores} onSelect={selectStore} />
        <Home
          store={store}
          carts={carts}
          multiStore={stores.length > 1}
          avatarInitials={avatarInitials}
          updatedAt={cartsQuery.dataUpdatedAt}
          onOpenSwitcher={() => setScreen('switcher')}
          onOpenCart={(i) => {
            track('card_click', ACTION_NAMES.openCart);
            setCartIdx(i);
            setScreen('detail');
          }}
          onToggleMenu={() => setMenuOpen((o) => !o)}
          onRefresh={() => {
            track('card_click', ACTION_NAMES.refresh);
            return cartsQuery.refetch();
          }}
        />
        {cart ? (
          <Detail storeName={store.name} cart={cart} onBack={() => setScreen('home')} />
        ) : (
          <div style={{ flex: '0 0 33.3333%' }} />
        )}
      </div>
      {accountMenu}
    </>
  );
}
